import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PLAN_DOC, PLAN_REVISIONS, HEADLINE, SECTIONS, MESSAGES } from './supportRolloutData';

const TONES = {
  green: { border: 'rgba(70,187,137,0.42)', bg: 'rgba(70,187,137,0.07)', color: 'var(--ni-green)' },
  amber: { border: 'rgba(217,180,74,0.5)', bg: 'rgba(217,180,74,0.07)', color: '#E8CD7E' },
  red: { border: 'rgba(224,96,96,0.5)', bg: 'rgba(224,96,96,0.07)', color: '#F09090' },
  neutral: { border: 'var(--ni-border-soft)', bg: 'rgba(255,255,255,0.025)', color: 'var(--ni-text-soft)' },
};

const KIND_TONE = {
  Adoption: '#8FBFE8',
  Quality: '#C7B2EC',
  Efficiency: '#E8CD7E',
  Outcome: '#6FD3A6',
};

function Block({ block }) {
  switch (block.type) {
    case 'p':
      return <p style={{ margin: '0 0 12px', fontSize: 13.5, lineHeight: 1.7, color: 'var(--ni-text-soft)' }}>{block.text}</p>;

    case 'list':
      return (
        <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: 'var(--ni-text-soft)' }}>
          {block.items.map((t, i) => <li key={i} style={{ marginBottom: 5 }}>{t}</li>)}
        </ul>
      );

    case 'numbered':
      return (
        <ol style={{ margin: '0 0 12px', paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: 'var(--ni-text-soft)' }}>
          {block.items.map((t, i) => <li key={i} style={{ marginBottom: 5 }}>{t}</li>)}
        </ol>
      );

    case 'callout': {
      const t = TONES[block.tone] || TONES.neutral;
      return (
        <div style={{
          margin: '0 0 14px', border: `1px solid ${t.border}`, background: t.bg,
          borderRadius: 14, padding: '12px 15px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: t.color, marginBottom: 5 }}>
            {block.title}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--ni-text-soft)' }}>{block.text}</div>
        </div>
      );
    }

    case 'cards':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 14 }}>
          {block.items.map((c, i) => (
            <div key={i} style={{
              border: '1px solid var(--ni-border-soft)', background: 'rgba(255,255,255,0.025)',
              borderRadius: 14, padding: '12px 15px',
            }}>
              {c.meta && (
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--ni-green)', marginBottom: 5 }}>
                  {c.meta}
                </div>
              )}
              <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 5 }}>{c.title}</div>
              <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-muted)' }}>{c.text}</div>
            </div>
          ))}
        </div>
      );

    case 'table':
      return (
        <div className="ni-table-wrap" style={{ marginBottom: 14 }}>
          <table className="ni-table">
            <thead>
              <tr>{block.columns.map(c => <th key={c}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} style={{
                      verticalAlign: 'top', fontSize: 12.5, lineHeight: 1.55,
                      fontWeight: j === 0 ? 600 : 400,
                      color: j === 0 ? 'var(--ni-text)' : 'var(--ni-text-soft)',
                    }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'rule':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 14 }}>
          <div style={{ border: '1px solid rgba(70,187,137,0.4)', background: 'rgba(70,187,137,0.06)', borderRadius: 14, padding: '13px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--ni-green)', marginBottom: 8 }}>
              ✓ Starts now
            </div>
            <ul style={{ margin: 0, paddingLeft: 17, fontSize: 12.5, lineHeight: 1.65, color: 'var(--ni-text-soft)' }}>
              {block.do.map((t, i) => <li key={i} style={{ marginBottom: 5 }}>{t}</li>)}
            </ul>
          </div>
          <div style={{ border: '1px solid rgba(224,96,96,0.42)', background: 'rgba(224,96,96,0.05)', borderRadius: 14, padding: '13px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#F09090', marginBottom: 8 }}>
              ✕ Stops now
            </div>
            <ul style={{ margin: 0, paddingLeft: 17, fontSize: 12.5, lineHeight: 1.65, color: 'var(--ni-text-soft)' }}>
              {block.dont.map((t, i) => <li key={i} style={{ marginBottom: 5 }}>{t}</li>)}
            </ul>
          </div>
        </div>
      );

    case 'timeline':
      return (
        <div style={{ display: 'grid', gap: 12, marginBottom: 14 }}>
          {block.items.map((it, i) => (
            <div key={it.phase} style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 14,
              border: '1px solid var(--ni-border-soft)', background: 'rgba(255,255,255,0.025)',
              borderRadius: 14, padding: '13px 16px',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{
                  width: 26, height: 26, borderRadius: '50%', flex: 'none',
                  border: '1px solid rgba(70,187,137,0.5)', background: 'rgba(70,187,137,0.13)',
                  color: 'var(--ni-green)', fontSize: 12, fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{i + 1}</span>
                {i < block.items.length - 1 && <span style={{ flex: 1, width: 0, borderLeft: '2px dashed var(--ni-border-soft)' }} />}
              </div>
              <div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 7 }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700 }}>{it.phase}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: 'var(--ni-green)', border: '1px solid rgba(70,187,137,0.45)', borderRadius: 999, padding: '2px 9px',
                  }}>{it.when}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--ni-text-muted)' }}>owner: {it.owner}</span>
                </div>
                <ul style={{ margin: '0 0 8px', paddingLeft: 17, fontSize: 12.5, lineHeight: 1.65, color: 'var(--ni-text-soft)' }}>
                  {it.what.map((w, j) => <li key={j} style={{ marginBottom: 3 }}>{w}</li>)}
                </ul>
                <div style={{ fontSize: 11.5, color: 'var(--ni-text-muted)' }}>
                  <strong style={{ color: 'var(--ni-text-soft)', letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 10 }}>Done when · </strong>
                  {it.exit}
                </div>
              </div>
            </div>
          ))}
        </div>
      );

    case 'metrics':
      return (
        <div style={{ display: 'grid', gap: 9, marginBottom: 14 }}>
          {block.items.map(m => (
            <div key={m.metric} style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 16px',
              border: '1px solid var(--ni-border-soft)', background: 'rgba(255,255,255,0.025)',
              borderRadius: 12, padding: '11px 14px',
            }}>
              <div>
                <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.11em', textTransform: 'uppercase',
                    color: KIND_TONE[m.kind] || 'var(--ni-text-muted)',
                    border: `1px solid ${KIND_TONE[m.kind] || 'var(--ni-border-soft)'}`,
                    borderRadius: 999, padding: '1px 7px', opacity: 0.9,
                  }}>{m.kind}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{m.metric}</div>
              </div>
              <div>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ni-text-muted)' }}>Target</div>
                <div style={{ fontSize: 12.5, color: 'var(--ni-green)', fontWeight: 600 }}>{m.target}</div>
              </div>
              <div>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ni-text-muted)' }}>Why</div>
                <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--ni-text-soft)' }}>{m.why}</div>
              </div>
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
}

function MessageCard({ msg }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(msg.body).then(() => setCopied(true), () => setCopied(false));
  }, [msg.body]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <div style={{
      border: '1px solid var(--ni-border-soft)', background: 'rgba(255,255,255,0.025)',
      borderRadius: 16, padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{msg.title}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ni-text-muted)', marginTop: 2 }}>{msg.meta}</div>
        </div>
        <button
          type="button"
          onClick={copy}
          className={`ni-button ${copied ? 'ni-button--primary' : 'ni-button--ghost'}`}
          style={{ fontSize: 12, minHeight: 34, whiteSpace: 'nowrap' }}
        >
          {copied ? '✓ Copied' : 'Copy text'}
        </button>
      </div>
      <pre style={{
        margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        fontFamily: 'inherit', fontSize: 12.5, lineHeight: 1.7, color: 'var(--ni-text-soft)',
        background: 'rgba(0,0,0,0.28)', border: '1px solid var(--ni-border-soft)',
        borderRadius: 12, padding: '13px 15px',
      }}>{msg.body}</pre>
    </div>
  );
}

export default function SupportRolloutPage() {
  const navigate = useNavigate();
  const [view, setView] = useState('plan');

  const jump = id => {
    const el = document.getElementById(`sec-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="ni-page">
      <div className="ni-shell">
        <header className="ni-header">
          <div className="ni-brand">
            <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Support Tools</span>
              <span className="ni-brand-title">Support System — Internal Rollout &amp; Communication Plan</span>
              <span className="ni-brand-subtitle">{PLAN_DOC.id} · {PLAN_DOC.revision} · {PLAN_DOC.status}</span>
            </div>
          </div>
          <div className="ni-toolbar">
            <button className="ni-button ni-button--ghost" onClick={() => navigate('/support')}>← Support Tools</button>
            <button className="ni-button ni-button--ghost" onClick={() => navigate('/support/flow')}>The flow</button>
          </div>
        </header>

        <div className="ni-panel ni-panel--dense">
          <div className="ni-tabs">
            {[['plan', 'The plan'], ['messages', 'Ready-to-send messages'], ['about', 'Revision & how this differs from the draft']].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setView(k)}
                className={`ni-tab${view === k ? ' ni-tab--active' : ''}`}
                style={{ padding: '0.45rem 0.85rem', fontSize: 13 }}
              >{label}</button>
            ))}
          </div>
        </div>

        {view === 'plan' && (
          <>
            <section className="ni-panel">
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ni-green)', marginBottom: 8 }}>
                {HEADLINE.eyebrow}
              </div>
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: 'var(--ni-text)' }}>{HEADLINE.text}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--ni-border-soft)' }}>
                {SECTIONS.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => jump(s.id)}
                    style={{
                      font: 'inherit', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                      background: 'rgba(255,255,255,0.03)', border: '1px solid var(--ni-border-soft)',
                      color: 'var(--ni-text-muted)', borderRadius: 999, padding: '4px 11px',
                    }}
                  >
                    <span style={{ color: 'var(--ni-green)', fontWeight: 800 }}>{s.n}</span> {s.title}
                  </button>
                ))}
              </div>
            </section>

            {SECTIONS.map(s => (
              <section key={s.id} id={`sec-${s.id}`} className="ni-panel">
                <div style={{ display: 'flex', gap: 13, alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--ni-green)', lineHeight: 1 }}>{s.n}</span>
                  <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>{s.title}</h2>
                </div>
                {s.lead && (
                  <p style={{ margin: '0 0 16px', fontSize: 13, lineHeight: 1.6, color: 'var(--ni-text-muted)' }}>{s.lead}</p>
                )}
                {s.blocks.map((b, i) => <Block key={i} block={b} />)}
              </section>
            ))}

            <div className="ni-panel">
              <div style={{
                border: '1px dashed rgba(217,180,74,0.55)', background: 'rgba(217,180,74,0.06)',
                borderRadius: 14, padding: '13px 16px',
              }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#E8CD7E', marginBottom: 6 }}>
                  ⚠ Before this is circulated
                </div>
                <ul style={{ margin: 0, paddingLeft: 17, fontSize: 12.5, lineHeight: 1.7, color: 'var(--ni-text-soft)' }}>
                  <li>Fill in the roles in section 6 — at minimum process owner, system owner and rollout contact.</li>
                  <li>Set the go-live date and convert the relative weeks in section 9 to real dates.</li>
                  <li>Confirm or change the response targets in section 8.</li>
                  <li>Define the out-of-hours route.</li>
                  <li>Settle the open questions on the flow document — the service-line mapping and the intake target number.</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {view === 'messages' && (
          <>
            <section className="ni-panel">
              <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Ready-to-send messages</h2>
              <p className="ni-section-copy" style={{ marginTop: 0 }}>
                Four artifacts covering the announcement itself and the week after. Written to be used with light editing —
                replace anything in square brackets. The manager talking points matter more than the all-hands text: people
                change how they work because their own manager said so, not because of an announcement.
              </p>
            </section>
            <div className="ni-stack">
              {MESSAGES.map(m => <MessageCard key={m.id} msg={m} />)}
            </div>
          </>
        )}

        {view === 'about' && (
          <>
            <section className="ni-panel">
              <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>What was kept, and what was changed</h2>
              <p className="ni-section-copy" style={{ marginTop: 0 }}>
                The six-point internal draft was structurally sound — objective, reasons, audience, expected behaviour,
                rollout, ownership. All six are here. The renaming to <em>Rollout &amp; Communication Plan</em> and the
                eight-section structure are as intended. Below is what was added or changed, and why.
              </p>
              <div className="ni-table-wrap">
                <table className="ni-table">
                  <thead>
                    <tr><th style={{ width: 230 }}>Change</th><th>Reasoning</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Softened the "no Teams, no email" rule</td>
                      <td>The draft said support cases should no longer be handled through Teams chats or direct messages. As an instruction it will be ignored, and being ignored on day one costs credibility for everything else in the plan. Replaced with a narrower rule that gets the same outcome: discuss anywhere, but the outcome goes on the case the same day.</td>
                    </tr>
                    <tr>
                      <td>Added an explicit scope boundary (section 3)</td>
                      <td>Without a stated line, people either log everything or decide the rule does not apply to them. A short two-column list removes the argument.</td>
                    </tr>
                    <tr>
                      <td>Added "what stops" alongside "what starts" (section 2)</td>
                      <td>The draft described the new behaviour. People need the old behaviour named as ended, or they will do both.</td>
                    </tr>
                    <tr>
                      <td>Added measurable success criteria (section 10)</td>
                      <td>The draft said to run the process for a period and collect feedback. That cannot be judged. Nine measures, split into adoption, quality, efficiency and outcome, make it possible to say whether it worked.</td>
                    </tr>
                    <tr>
                      <td>Added response expectations (section 8)</td>
                      <td>Nobody can comply with an unstated standard, and no customer promise is credible without one. Also what makes priority classification worth setting.</td>
                    </tr>
                    <tr>
                      <td>Added an out-of-hours and urgency path</td>
                      <td>The most reliable way to lose adoption is to leave people with no fast route when something is genuinely urgent. They will find one, and it will be outside the system.</td>
                    </tr>
                    <tr>
                      <td>Named leadership behaviour explicitly (section 4)</td>
                      <td>"What is the case number?" as a manager's standard first question drives adoption harder than training does, because it changes which path is easier.</td>
                    </tr>
                    <tr>
                      <td>Added the surveillance concern head-on (section 5)</td>
                      <td>People will privately suspect it. Unaddressed, it produces thin case notes and quiet resistance. Saying "reporting is on causes, never individuals" in the announcement is cheap and effective.</td>
                    </tr>
                    <tr>
                      <td>Split process owner from system owner (section 6)</td>
                      <td>The draft correctly separated these. Reinforced, because "the system won't let me" and "the process says I must" are different problems and get conflated constantly.</td>
                    </tr>
                    <tr>
                      <td>Added a rollout risk register (section 13)</td>
                      <td>Nine predictable failure modes, each with a cheap mitigation if planned now rather than diagnosed later.</td>
                    </tr>
                    <tr>
                      <td>Added a 30-day amnesty, then enforcement (section 9)</td>
                      <td>Enforcing from day one produces compliance theatre. Helping for a month and then treating it as a normal expectation produces adoption.</td>
                    </tr>
                    <tr>
                      <td>Added data, access and privacy (section 14)</td>
                      <td>Cases contain named people at customer and partner organisations, and partner visibility has to be decided before go-live rather than after a complaint.</td>
                    </tr>
                    <tr>
                      <td>Added the ISO argument</td>
                      <td>Gives the plan a reason beyond internal preference: ISO 9001 already requires customer feedback and complaints to be handled and recorded. Doing it this way means the same effort produces audit evidence.</td>
                    </tr>
                    <tr>
                      <td>Added ready-to-send artifacts</td>
                      <td>A plan nobody has to translate into words gets communicated. Announcement, manager card, Teams post, desk card.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="ni-panel">
              <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Document control</h2>
              <div className="ni-table-wrap">
                <table className="ni-table">
                  <tbody>
                    <tr><td style={{ width: 200, color: 'var(--ni-text-muted)' }}>Document ID</td><td style={{ fontWeight: 700 }}>{PLAN_DOC.id}</td></tr>
                    <tr><td style={{ color: 'var(--ni-text-muted)' }}>Revision</td><td>{PLAN_DOC.revision} · {PLAN_DOC.status} · {PLAN_DOC.date}</td></tr>
                    <tr><td style={{ color: 'var(--ni-text-muted)' }}>Owner</td><td>{PLAN_DOC.owner}</td></tr>
                    <tr><td style={{ color: 'var(--ni-text-muted)' }}>Related document</td><td>{PLAN_DOC.relatedFlow}</td></tr>
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
                {PLAN_REVISIONS.map(r => (
                  <div key={r.rev} style={{
                    border: '1px solid var(--ni-border-soft)', background: 'rgba(255,255,255,0.02)',
                    borderRadius: 14, padding: '13px 16px',
                  }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--ni-green)' }}>{r.rev}</span>
                      <span style={{ fontSize: 12, color: 'var(--ni-text-muted)' }}>{r.date}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                        color: 'var(--ni-text-soft)', border: '1px solid var(--ni-border-soft)', borderRadius: 999, padding: '2px 9px',
                      }}>{r.status}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 7 }}>{r.summary}</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ni-text-soft)' }}>
                      {r.changes.map((c, i) => <li key={i} style={{ marginBottom: 2 }}>{c}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
